import React, { useState, useEffect, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Input from '@amzn/meridian/input';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Pagination from '@amzn/meridian/pagination';
import SearchField from '@amzn/meridian/search-field';
import { getNodeConfig, getDSData, callApi } from '../services/api';
import { NODE_CONFIG_TABLE, CAP_RELEASE_CONFIG_TABLE, API_STAGE } from '../appConfig';

const ALLOWED_TYPES = ['AMZL', 'AMZL_XPT'];

const UvpNiBuffer: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [bufferValues, setBufferValues] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage] = useState(20);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [nodeResult, configResult] = await Promise.all([
                getNodeConfig(NODE_CONFIG_TABLE),
                getDSData(CAP_RELEASE_CONFIG_TABLE, {}),
            ]);

            const nodeItems = Array.isArray(nodeResult?.items || nodeResult) ? (nodeResult?.items || nodeResult) : [];
            const configItems = Array.isArray(configResult?.items || configResult) ? (configResult?.items || configResult) : [];

            // Filter by type and get unique location_id + cycle_name
            const filtered = nodeItems.filter((item: any) => ALLOWED_TYPES.includes(item.type));
            const uniqueMap = new Map<string, any>();
            for (const item of filtered) {
                const key = `${item.location_id}#${item.cycle_name}`;
                if (!uniqueMap.has(key)) {
                    uniqueMap.set(key, { location_id: item.location_id, cycle_name: item.cycle_name, type: item.type, parent: item.parent });
                }
            }
            setData(Array.from(uniqueMap.values()));

            // Load existing uvp_ni_buffer values from cap-release-config
            const buffers: Record<string, string> = {};
            for (const item of configItems) {
                if (item.metric_name === 'uvp_ni_buffer') {
                    const key = `${item.location_id}#${item.cycle_name}`;
                    buffers[key] = item.metric_value || '';
                }
            }
            setBufferValues(buffers);
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { setCurrentPage(1); }, [filterText]);

    const getRowKey = (item: any) => `${item.location_id}#${item.cycle_name}`;

    const handleEdit = (item: any) => {
        const key = getRowKey(item);
        setEditingKey(key);
        setEditValue(bufferValues[key] || '');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const handleSave = async (item: any) => {
        setIsSaving(true);
        setError(null);
        try {
            const compositeKey = `${item.location_id}#${item.cycle_name}#uvp_ni_buffer`;
            await callApi({
                method: 'update_config',
                apiRoute: `${API_STAGE}/config`,
                table_name: CAP_RELEASE_CONFIG_TABLE,
                items: [{
                    composite_key: compositeKey,
                    business: 'AMZL',
                    horizon: 'D-1',
                    location_id: item.location_id,
                    type: '',
                    cycle_name: item.cycle_name,
                    sort_code: '',
                    cpt: '',
                    metric_name: 'uvp_ni_buffer',
                    metric_value: editValue,
                }],
                key_columns: ['composite_key'],
            });
            setEditingKey(null);
            setEditValue('');
            setSuccess('UVP NI Buffer saved');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    const filteredData = filterText
        ? data.filter(item =>
            Object.values(item).some(v => String(v).toLowerCase().includes(filterText.toLowerCase()))
        )
        : data;

    const totalPages = Math.max(1, Math.ceil(filteredData.length / resultsPerPage));
    const paginatedData = filteredData.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage);

    if (isLoading) return <Row alignmentHorizontal="center" spacingInset="450"><Loader size="small" /></Row>;

    return (
        <Column spacing="400">
            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <Row alignmentHorizontal="justify" alignmentVertical="bottom">
                <Text type="h100">{filteredData.length} records (AMZL / AMZL_XPT)</Text>
                <Row spacing="200">
                    <SearchField value={filterText} onChange={setFilterText} onSubmit={() => {}} placeholder="Search..." width={200} />
                    <Button size="small" onClick={loadData}>Refresh</Button>
                </Row>
            </Row>

            <Table headerRows={1} showDividers>
                <TableRow highlightOnHover={false}>
                    <TableCell><Text type="b200" color="secondary">Node</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Parent</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Cycle</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">UVP NI Buffer %</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Actions</Text></TableCell>
                </TableRow>
                {paginatedData.map((item, idx) => {
                    const key = getRowKey(item);
                    return (
                        <TableRow key={key + idx} highlightOnHover>
                            <TableCell><Text type="b200">{item.location_id}</Text></TableCell>
                            <TableCell><Text type="b200">{item.parent || '-'}</Text></TableCell>
                            <TableCell><Text type="b200">{item.cycle_name}</Text></TableCell>
                            <TableCell><Text type="b200">{item.type}</Text></TableCell>
                            <TableCell>
                                {editingKey === key ? (
                                    <Input value={editValue} onChange={setEditValue} type="number" size="small" width={80} />
                                ) : (
                                    <Text type="b200">{bufferValues[key] || '-'}</Text>
                                )}
                            </TableCell>
                            <TableCell>
                                {editingKey === key ? (
                                    <Row spacing="200">
                                        <Button size="small" type="primary" onClick={() => handleSave(item)} disabled={isSaving}>
                                            {isSaving ? '...' : 'Save'}
                                        </Button>
                                        <Button size="small" onClick={handleCancel}>Cancel</Button>
                                    </Row>
                                ) : (
                                    <Button size="small" onClick={() => handleEdit(item)}>Edit</Button>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Table>

            {totalPages > 1 && (
                <Row alignmentHorizontal="center">
                    <Pagination numberOfPages={totalPages} currentPage={currentPage} onChange={setCurrentPage} />
                </Row>
            )}
        </Column>
    );
};

export default UvpNiBuffer;
